import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import type { Campaign } from '../../store/appStore';
import { Button } from '../ui/button';

interface Props {
  accountId: string;
  accountHandle: string;
}

const TYPE_LABELS: Record<string, string> = {
  poster: 'Plakat',
  debate: 'Debatt',
  vote: 'Avstemning',
};

function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[æ]/g, 'ae').replace(/[ø]/g, 'oe').replace(/[å]/g, 'aa')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

const CampaignRow: React.FC<{ campaign: Campaign; handle: string }> = ({ campaign, handle }) => {
  const navigate = useNavigate();
  const isClosed = !!campaign.closesAt;

  return (
    <div
      onClick={() => navigate(`/dashboard/campaign/${campaign.id}`)}
      className="flex items-center justify-between p-4 bg-[#2a2a2a] rounded-lg cursor-pointer hover:bg-[#333] transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-white font-medium truncate">{campaign.title}</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`w-2 h-2 rounded-full ${isClosed ? 'bg-red-500' : 'bg-green-400'}`} />
        <span className="text-gray-400 text-sm">&rsaquo;</span>
      </div>
    </div>
  );
};

export const CampaignManager: React.FC<Props> = ({ accountId, accountHandle }) => {
  const { fetchCampaignsByAccount, createCampaign, saveVoteOptions } = useAppStore();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  // Create form state
  const [newType, setNewType] = useState<'poster' | 'debate' | 'vote'>('poster');
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newOptions, setNewOptions] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = async () => {
    const list = await fetchCampaignsByAccount(accountId).catch(() => []);
    setCampaigns(list);
    setIsLoading(false);
  };

  useEffect(() => { load(); }, [accountId]);

  const handleTitleChange = (val: string) => {
    setNewTitle(val);
    setNewSlug(slugify(val));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || !newSlug.trim()) return;
    setIsSaving(true);
    try {
      const campaign = await createCampaign(accountId, {
        slug: newSlug,
        type: newType,
        title: newTitle.trim(),
        description: newDesc.trim() || null,
        closesAt: null,
      });

      if (newType === 'vote' && newOptions.trim()) {
        const labels = newOptions.split('\n').map(l => l.trim()).filter(Boolean);
        await saveVoteOptions(campaign.id, labels);
      }

      setNewTitle(''); setNewDesc(''); setNewSlug(''); setNewOptions('');
      setShowCreate(false);
      await load();
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <p className="text-gray-500 text-sm">Laster kampanjer...</p>;
  }

  const posters = campaigns.filter(c => c.type === 'poster');
  const debates = campaigns.filter(c => c.type === 'debate');
  const votes = campaigns.filter(c => c.type === 'vote');

  return (
    <div className="space-y-6">
      {/* Plakater section */}
      {posters.length > 0 && (
        <div>
          <h3 className="text-white/50 text-xs uppercase tracking-wider font-semibold mb-3">
            Plakater ({posters.length})
          </h3>
          <div className="space-y-2">
            {posters.map(c => (
              <CampaignRow key={c.id} campaign={c} handle={accountHandle} />
            ))}
          </div>
        </div>
      )}

      {/* Debatt section */}
      {debates.length > 0 && (
        <div>
          <h3 className="text-white/50 text-xs uppercase tracking-wider font-semibold mb-3">
            Debatter ({debates.length})
          </h3>
          <div className="space-y-2">
            {debates.map(c => (
              <CampaignRow key={c.id} campaign={c} handle={accountHandle} />
            ))}
          </div>
        </div>
      )}

      {/* Avstemning section */}
      {votes.length > 0 && (
        <div>
          <h3 className="text-white/50 text-xs uppercase tracking-wider font-semibold mb-3">
            Avstemninger ({votes.length})
          </h3>
          <div className="space-y-2">
            {votes.map(c => (
              <CampaignRow key={c.id} campaign={c} handle={accountHandle} />
            ))}
          </div>
        </div>
      )}

      {campaigns.length === 0 && !showCreate && (
        <p className="text-gray-500 text-sm py-2">Ingen kampanjer ennå.</p>
      )}

      {/* Create new campaign */}
      {showCreate ? (
        <form onSubmit={handleCreate} className="bg-[#2a2a2a] rounded-lg p-4 space-y-3">
          <p className="text-white font-semibold text-sm">Ny kampanje</p>

          <div className="flex gap-2">
            {(['poster', 'debate', 'vote'] as const).map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setNewType(t)}
                className={`flex-1 text-xs py-2 rounded transition-colors ${
                  newType === t ? 'bg-white text-black' : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {TYPE_LABELS[t]}
              </button>
            ))}
          </div>

          <input
            value={newTitle}
            onChange={e => handleTitleChange(e.target.value)}
            required
            placeholder="Tittel på kampanjen"
            className="w-full p-3 rounded-lg bg-[#1a1a1a] text-white border-none focus:ring-2 focus:ring-white/30 text-sm"
          />

          <input
            value={newSlug}
            onChange={e => setNewSlug(e.target.value)}
            required
            placeholder="slug-i-url"
            className="w-full p-3 rounded-lg bg-[#1a1a1a] text-white/70 border-none focus:ring-2 focus:ring-white/30 text-xs font-mono"
          />

          <textarea
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            rows={2}
            placeholder="Beskrivelse (valgfritt)"
            className="w-full p-3 rounded-lg bg-[#1a1a1a] text-white border-none focus:ring-2 focus:ring-white/30 text-sm resize-none"
          />

          {newType === 'vote' && (
            <div>
              <p className="text-gray-400 text-xs mb-2">Stemmevalg (ett per linje):</p>
              <textarea
                value={newOptions}
                onChange={e => setNewOptions(e.target.value)}
                rows={4}
                placeholder={'Ja\nNei\nVet ikke'}
                className="w-full p-3 rounded-lg bg-[#1a1a1a] text-white border-none focus:ring-2 focus:ring-white/30 text-sm resize-none"
              />
            </div>
          )}

          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={isSaving || !newTitle.trim()}
              className="bg-white text-black hover:bg-gray-200 text-sm"
            >
              {isSaving ? 'Oppretter...' : 'Opprett'}
            </Button>
            <Button
              type="button"
              onClick={() => setShowCreate(false)}
              className="bg-white/10 hover:bg-white/20 text-white text-sm"
            >
              Avbryt
            </Button>
          </div>
        </form>
      ) : (
        <Button
          onClick={() => setShowCreate(true)}
          className="w-full bg-white/10 hover:bg-white/20 text-white py-3"
        >
          + Ny kampanje
        </Button>
      )}
    </div>
  );
};
